import { useQuery, useMutation } from '@tanstack/react-query';
import { apiService } from '../services/api';

// STD-015: server state for the Accept Invitation flow.
// apiService is imported here (a *.queries.ts file) per STD-016.
// Errors surface to the component via isError / error for inline Alert UI;
// the global toast is skipped because the page renders its own error state.

export interface InvitationValidation {
  valid: boolean;
  email?: string;
  role?: string;
  organization_name?: string;
  error?: string;
}

export const invitationKeys = {
  validate: (token: string) => ['invitation', 'validate', token] as const,
};

export function useValidateInvitationQuery(token: string | null) {
  return useQuery<InvitationValidation, Error>({
    queryKey: invitationKeys.validate(token ?? ''),
    enabled: token != null && token.length > 0,
    retry: false,
    // The token is a single-use credential; don't retain it in the cache after
    // the page unmounts (keeps it out of long-lived cache / devtools state).
    gcTime: 0,
    // Component renders its own error UI — skip the global toast.
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => {
      const result = await apiService.validateInvitation(token!);
      return result as InvitationValidation;
    },
  });
}

export interface AcceptInvitationInput {
  token: string;
  password: string;
  fullName: string;
}

export function useAcceptInvitationMutation() {
  return useMutation<void, Error, AcceptInvitationInput>({
    // Component renders its own error state (formErrors) — skip the global toast.
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({ token, password, fullName }: AcceptInvitationInput) => {
      await apiService.acceptInvitationAndCreateUser(token, password, fullName);
    },
  });
}
